import React from 'react';
import { Link } from 'react-router-dom';
import { Users, MessageCircle, Calendar, Award, Target, TrendingUp, Star, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Community: React.FC = () => {
  const communityFeatures = [
    {
      icon: Users,
      title: "Community Pods",
      description: "Join small groups of entrepreneurs for focused accountability and support"
    },
    {
      icon: MessageCircle,
      title: "Real-time Chat",
      description: "Connect instantly with your pod members through our in-app messaging system"
    },
    {
      icon: Calendar,
      title: "Weekly Meetups",
      description: "Attend virtual meetups to share progress, challenges, and celebrate wins"
    },
    {
      icon: Award,
      title: "Recognition System",
      description: "Earn badges and recognition for milestones and helping others succeed"
    }
  ];

  const podBenefits = [
    "Accountability partners who understand your journey",
    "Diverse perspectives from different industries",
    "Shared resources and knowledge exchange",
    "Motivation through collective progress",
    "Problem-solving through group brainstorming",
    "Long-term networking and friendships"
  ];

  const successStories = [
    {
      name: "Tech Pod Alpha",
      achievements: ["3 successful launches", "₹10L+ combined revenue", "2 partnerships formed"],
      story: "Our pod started as strangers and became business partners. We meet weekly and have launched 3 successful products together."
    },
    {
      name: "E-commerce Warriors",
      achievements: ["5 stores launched", "₹15L+ combined revenue", "Shared supplier network"],
      story: "We share everything from suppliers to marketing strategies. The collective knowledge is incredible."
    },
    {
      name: "SaaS Sprinters",
      achievements: ["2 SaaS products", "₹15L+ MRR", "Cross-promotion success"],
      story: "We help each other with technical challenges and marketing. It's like having a co-founder without the equity split."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400 text-white pt-32 pb-40">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-6xl md:text-7xl font-light tracking-tight mb-8 leading-tight">
              Join the
              <br />
              <span className="font-bold">Sprint Community</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed max-w-2xl mx-auto font-light">
              Connect with fellow entrepreneurs, join accountability pods, and accelerate your success through collective wisdom
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                  Join a Pod
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/docs#community-pods">
                <Button size="lg" variant="outline" className="border-2 border-white/80 text-black hover:bg-white hover:text-teal-600 text-lg px-12 py-4 font-semibold backdrop-blur-sm">
                  Learn More
                </Button>
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">500+</div>
                <div className="text-black-400 text-sm">Members</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">10+</div>
                <div className="text-black-400 text-sm">Active Pods</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">₹30L+</div>
                <div className="text-black-400 text-sm">Revenue Generated</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Features */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Why Join Our Community?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              Entrepreneurship can be lonely. Our community makes it collaborative and fun.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {communityFeatures.map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:border-blue-300 transition-colors">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-medium mb-4 text-black">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pod System */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-5xl md:text-6xl font-light mb-8">Community Pods</h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                We automatically group you with 3-5 entrepreneurs who are at similar stages and have complementary skills. 
                Your pod becomes your accountability group, sounding board, and support network.
              </p>
              
              <div className="space-y-6 mb-8">
                {podBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <Star className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 leading-relaxed">{benefit}</p>
                  </div>
                ))}
              </div>
              
              <Link to="/signup">
                <Button size="lg" className="bg-black text-white hover:bg-gray-800 text-lg px-8 py-4 font-medium">
                  Get Matched with a Pod
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            
            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <h3 className="text-2xl font-medium text-black mb-6">How Pods Work</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-black mb-1">Smart Matching</h4>
                    <p className="text-gray-600 text-sm">Our algorithm matches you with compatible entrepreneurs</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-black mb-1">Weekly Check-ins</h4>
                    <p className="text-gray-600 text-sm">Regular meetings to share progress and challenges</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-black mb-1">Continuous Support</h4>
                    <p className="text-gray-600 text-sm">Ongoing collaboration and knowledge sharing</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Pod Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              See how our community pods are helping entrepreneurs achieve incredible results together
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {successStories.map((story, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-medium text-black">{story.name}</h3>
                </div>
                
                <div className="mb-6">
                  <h4 className="font-medium text-black mb-3">Key Achievements:</h4>
                  <ul className="space-y-2">
                    {story.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-600">
                        <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <p className="text-gray-700 leading-relaxed italic">
                  "{story.story}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Events */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Community Events</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              Regular events to learn, network, and celebrate success together
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
              <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-medium mb-4 text-black">Weekly Pod Meetups</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Regular check-ins with your pod members to share progress and get feedback
              </p>
              <div className="text-sm text-blue-600 font-medium">Every Week</div>
            </div>
            
            <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
              <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-medium mb-4 text-black">Monthly Masterminds</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Deep-dive sessions on specific topics with industry experts and successful entrepreneurs
              </p>
              <div className="text-sm text-blue-600 font-medium">Monthly</div>
            </div>
            
            <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
              <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-medium mb-4 text-black">Quarterly Retreats</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                In-person networking events and intensive workshops for community members
              </p>
              <div className="text-sm text-blue-600 font-medium">Quarterly</div>
            </div>
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8">
              Ready to Join the Community?
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-blue-200 font-light">
              Connect with entrepreneurs who understand your journey and want you to succeed
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                  Join Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/docs#community-pods">
                <Button size="lg" variant="outline" className="border-2 border-white/80 text-black hover:bg-white hover:text-teal-600 text-lg px-12 py-4 font-semibold backdrop-blur-sm">
                  Learn More
                </Button>
              </Link>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">Free</div>
                <div className="text-blue-200 text-sm">Community Access</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">Instant</div>
                <div className="text-blue-200 text-sm">Pod Matching</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">24/7</div>
                <div className="text-blue-200 text-sm">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Community;
